// housePhrases.js — the measured ledger of the model's own worn-out narration,
// rendered for the prompt (2026-10-06).
//
// Haley: "narrative thought/narration has been very repetitive". Measured over
// 716 replies (120 days): 94% carried a narration phrase found in three or
// more other replies, against 35–37% for the writers' own messages, and the
// phrases were the same across characters. The guards in director.js work on
// one thread's last ten replies, so a house vocabulary starts clean in every
// new thread; the hand-written cliché ledger there was read four entries per
// turn, one turn in seven for any one phrase.
//
// The house-phrases edge function measures the model's OWN replies weekly and
// writes house_phrase_ledger (phrases + counts, no text). Two readers:
//   • renderHousePhrases → a standing block in the CACHED half of every
//     character's prompt (characterIdentity.js), after the voice laws. Byte-
//     stable between weekly runs, so it costs nothing per turn.
//   • housePhraseListFromLedger → the list the director rotates through at
//     depth 0 in place of its hand-written ledger (director.js rule 5).
// The permission side is spelled out, as every ban here must be: what to
// reach for instead.

export const HOUSE_PHRASE_PROMPT_MAX = 12;
export const HOUSE_PHRASE_MIN = 4;

function displayPhrase(p) {
  return String(p || '').trim().replace(/\bNAME\b/g, '[name]');
}

/** The ledger's phrases as plain strings, most repeated first, capped. */
export function housePhraseListFromLedger(ledger, max = HOUSE_PHRASE_PROMPT_MAX) {
  const rows = Array.isArray(ledger?.phrases) ? ledger.phrases : [];
  const out = [];
  for (const r of rows) {
    const p = displayPhrase(typeof r === 'string' ? r : r?.phrase);
    if (p && !out.includes(p)) out.push(p);
    if (out.length >= max) break;
  }
  return out;
}

/** One character's own measured tics (phrases in a notable share of THEIR replies), capped at three. */
export function characterPhraseListFromLedger(ledger, charId, max = 3) {
  if (!charId || !Array.isArray(ledger?.by_character)) return [];
  const row = ledger.by_character.find((r) => r && r.character_id === charId);
  const rows = Array.isArray(row?.phrases) ? row.phrases : [];
  const out = [];
  for (const r of rows) {
    const p = displayPhrase(typeof r === 'string' ? r : r?.phrase);
    if (p && !out.includes(p)) out.push(p);
    if (out.length >= max) break;
  }
  return out;
}

/** The standing block for the cached half. Empty string when the ledger is too thin to name. */
export function renderHousePhrases(ledger, charName = '') {
  const list = housePhraseListFromLedger(ledger);
  if (list.length < HOUSE_PHRASE_MIN) return '';
  const share = Math.round(Number(ledger?.share_with_house_phrase || 0) * 100);
  const n = Number(ledger?.replies || 0);
  const days = Number(ledger?.window_days || 0);
  const who = String(charName || '').trim();
  const whose = who ? `${who}'s and nobody else's` : `this character's and nobody else's`;
  return `NARRATION THIS APP HAS WORN OUT, measured, not guessed. Over the last ${days} days, across ${n} replies by every character in this app, ${share}% carried at least one of these:
${list.map((p) => `"${p}"`).join(' · ')}
They are the model's house phrases, not your writers', and they make every character read as the same narrator. Each one is a placeholder standing where a real detail should be. Reach instead for what belongs to THIS room and THIS body: an object that is actually here, a habit that is ${whose}, the plain word your writer would have used. One particular, plainly said, is worth more than any of these.`;
}
